import Link from 'next/link';
import { useRouter } from 'next/router';
import React from 'react';
import styles from '../../../styles/components/layout/HeaderSection.module.css';
import HeaderBrand from './HeaderBrand';
import HeaderContact from './HeaderContact';

/**
 * Mobile Header Component
 * Displays header content optimized for mobile screens
 * 
 * @param {Object} data - Header data object
 * @param {String} logoUrl - Logo image URL
 * @param {String} alternativeText - Logo alt text
 * @param {Array} menu - Menu items array
 * @param {Boolean} isMenuVisible - Whether menu should be visible
 * @param {Boolean} isMenuOpen - Whether mobile menu is open
 * @param {Function} toggleMenu - Function to toggle mobile menu
 * @param {Function} closeNav - Function to close mobile menu
 * @param {Function} handleNavigation - Function to handle navigation
 * @param {Object} navbarStyle - Navbar style object
 * @param {Boolean} isDarkMode - Dark mode state
 * @param {String} className - Additional CSS classes
 */
const MobileHeader = ({ 
    data, 
    logoUrl, 
    alternativeText, 
    menu, 
    isMenuVisible, 
    isMenuOpen, 
    toggleMenu, 
    closeNav, 
    handleNavigation, 
    navbarStyle, 
    isDarkMode, 
    className = '' 
}) => {
    const router = useRouter();

    // Function to check if menu item is active
    const isMenuItemActive = menuUrl => {
        const currentPath = router.asPath;
        if (currentPath === menuUrl) return true;
        if (menuUrl === '/' && currentPath === '/') return true;
        return menuUrl !== '/' && currentPath.startsWith(menuUrl);
    };
    return (
        <nav 
            id="navigation" 
            className={`navbar navbar-expand-lg fixed-top ${styles.navbar} ${isDarkMode ? styles['dark-navbar'] : ''} ${className}`} 
            style={navbarStyle} 
            role="navigation" 
            aria-label="Main navigation"
        >
            <div className="container color-white py-2 position-relative align-items-center bg-dark-grey" style={{ height: '4.75rem' }}>
                <button
                    className="navbar-toggler bg-white"
                    onClick={toggleMenu}
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#navbarSupportedContent"
                    aria-controls="navbarSupportedContent"
                    aria-expanded="false"
                    aria-label="Toggle navigation"
                >
                    <span className="navbar-toggler-icon"></span>
                </button>

                <HeaderBrand 
                    logoUrl={logoUrl}
                    alternativeText={alternativeText}
                    isMobile={true}
                />

                <HeaderContact 
                    contact={data.Contact}
                    isMobile={true}
                />

                {isMenuVisible && (
                    <nav
                        className={`navbar-collapse ${styles.sidenav} ${isMenuOpen ? `${styles['navbar-drop']} collapsed w-100` : 'collapse w-0'}`}
                        id="mySidenav"
                    >
                        <button className={styles.closebtn} onClick={closeNav}>
                            &times;
                        </button>
                        <ul className="navbar-nav ms-auto mb-2 mb-lg-0 mt-3">
                            {menu.map((item, index) => {
                                if (!item.isHidden) {
                                    const isActive = isMenuItemActive(item.url);
                                    return (
                                        <li className="nav-item ms-2 d-flex align-items-center justify-content-start" key={index}>
                                            <Link 
                                                href={item.url} 
                                                className={`nav-link ${styles['menu-links']} ${isActive ? styles.isActive : ''}`}
                                                onClick={() => handleNavigation(item.url)}
                                            >
                                                {item.name}
                                            </Link>
                                        </li>
                                    );
                                }
                                return null;
                            })}
                        </ul>
                    </nav>
                )}
            </div>
        </nav>
    );
};

export default MobileHeader;
